import { useCallback, useEffect, useRef, useState } from "react";
import { createPaymentMethodUpdateSession } from "@lemonadetraining/web-shared";
import { CARD_UPDATE_FAILED_FALLBACK } from "../components/UserProfile/dunningCopy";

export type CardUpdateRedirectStatus = "idle" | "minting" | "error";

export interface CardUpdateRedirect {
  start: () => Promise<void>;
  status: CardUpdateRedirectStatus;
  /** The server's own words when the page could not be opened. */
  errorMessage: string | null;
}

/**
 * Mints a card-update session for the member's own subscription and hands the
 * browser to Stripe. Success is never claimed from here: the redirect back
 * lands on a report-only page and the listener delivers the real outcome.
 */
export function useCardUpdateRedirect(subscriptionId: string): CardUpdateRedirect {
  const [status, setStatus] = useState<CardUpdateRedirectStatus>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const inFlight = useRef(false);

  // After a successful handoff the control stays spent: the browser is leaving.
  // Back-navigation can restore this page from the cache with that state
  // intact, and `pageshow` is the one signal that it is live again.
  useEffect(() => {
    const release = () => {
      inFlight.current = false;
      setStatus("idle");
    };
    window.addEventListener("pageshow", release);
    return () => window.removeEventListener("pageshow", release);
  }, []);

  const start = useCallback(async () => {
    if (inFlight.current || !subscriptionId) return;
    inFlight.current = true;
    setStatus("minting");
    setErrorMessage(null);
    try {
      const { url } = await createPaymentMethodUpdateSession(subscriptionId);
      window.location.assign(url);
    } catch (error) {
      inFlight.current = false;
      setStatus("error");
      const message = (error as { message?: string })?.message?.trim() ?? "";
      // A bare callable code such as "internal" is not copy; the server's own sentences are.
      setErrorMessage(message.includes(" ") ? message : CARD_UPDATE_FAILED_FALLBACK);
    }
  }, [subscriptionId]);

  return { start, status, errorMessage };
}
